/**
 * Lexical Rich Text Editor Component for Notifications
 * Integrates with react-hook-form
 * Replaces Draft.js Editor
 */

import { useEffect, useRef } from 'react';

import { TextArea } from '@dec/shared';
import { Col } from '@dec/shared';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { $getRoot, $insertNodes, type EditorState, type LexicalEditor } from 'lexical';
import type React from 'react';
import { Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ToolbarPlugin } from '../../../Parameters/TermsTemplate/ToolbarPlugin';

import './lexical-editor.scss';

interface LexicalEditorComponentProps {
  name: string;
}

export const LexicalEditorComponent: React.FC<LexicalEditorComponentProps> = ({ name }) => {
  const {
    formState: { errors },
    setValue,
    watch,
    clearErrors
  } = useFormContext();
  const { t } = useTranslation();
  const editorRef = useRef<LexicalEditor | null>(null);
  const isInitializedRef = useRef(false);

  const value = watch(name);
  const errorContent = errors['content'];

  const initialConfig = {
    namespace: 'NotificationEditor',
    theme: {
      paragraph: 'editor-paragraph',
      text: {
        bold: 'editor-text-bold',
        italic: 'editor-text-italic',
        underline: 'editor-text-underline'
      },
      link: 'editor-link',
      list: {
        ul: 'editor-list-ul',
        ol: 'editor-list-ol',
        listitem: 'editor-listitem'
      }
    },
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    nodes: [HeadingNode, ListNode, ListItemNode, QuoteNode, LinkNode, AutoLinkNode]
  };

  // Load initial HTML content
  useEffect(() => {
    if (editorRef.current && value && !isInitializedRef.current) {
      isInitializedRef.current = true;

      editorRef.current.update(() => {
        try {
          const parser = new DOMParser();
          const dom = parser.parseFromString(value, 'text/html');
          const nodes = $generateNodesFromDOM(editorRef.current!, dom);

          // Clear existing content
          const root = $getRoot();
          root.clear();

          // Insert new nodes
          $insertNodes(nodes);
        } catch (error) {
          console.error('Failed to load HTML into editor:', error);
        }
      });
    }
  }, [value]);

  // Handle editor changes
  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    editorState.read(() => {
      const html = $generateHtmlFromNodes(editor);
      const currentContent = editorState._nodeMap;
      const hasText = html.trim().length > 0;

      setValue(name, html);

      if (hasText && errorContent) {
        clearErrors(name);
      }
    });
  };

  return (
    <>
      <Col className="d-none">
        <TextArea label="hide" name={name} rows={5} />
      </Col>
      <LexicalComposer initialConfig={initialConfig}>
        <div className={`terms-wrapper ${errorContent ? 'is-invalid' : ''}`}>
          <ToolbarPlugin editorRef={editorRef} />
          <div className="terms-editor-container">
            <RichTextPlugin
              contentEditable={<ContentEditable className="terms-editor" />}
              placeholder={<div className="editor-placeholder">Enter message content...</div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <OnChangePlugin onChange={handleChange} />
            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin />
          </div>
        </div>
      </LexicalComposer>
      <div className="is-invalid">
        <div className="invalid-feedback">
          {errorContent && typeof errorContent === 'object' && 'message' in errorContent
            ? String(errorContent.message || '')
            : ''}
        </div>
      </div>
      <Form.Control.Feedback type="invalid">
        {errorContent && typeof errorContent === 'object' && 'message' in errorContent
          ? String(errorContent.message || '')
          : ''}
      </Form.Control.Feedback>
    </>
  );
};
